// Single place to read the stored session. The role check was previously
// duplicated inline in the routes and in the sidebar.
//
// Roles are no longer a fixed list: an admin can create their own, so nothing
// here may branch on a role *name*. What a user may do comes from the flat
// list of permission keys the server returns at login and on every session
// refresh, which is what `can()` reads. Which of the app's two shells they
// land in comes from the role's `portal`.

export function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("user"));
  } catch (error) {
    console.error("Error parsing user from localStorage:", error);
    return null;
  }
}

export function isAuthenticated() {
  return localStorage.getItem("token") !== null;
}

export function getRoleName(user = getStoredUser()) {
  return user?.role_id?.name ?? null;
}

/**
 * Stores everything the session needs after a login or a refresh.
 *
 * Permissions are kept separately from the user record because they are the
 * server's answer about this session, not a property of the person — writing
 * them in one place keeps the two from drifting apart.
 */
export function storeSession({ user, token, permissions, portal }) {
  if (token) localStorage.setItem("token", token);
  if (user) localStorage.setItem("user", JSON.stringify(user));
  localStorage.setItem("permissions", JSON.stringify(permissions || []));
  localStorage.setItem("portal", portal || user?.role_id?.portal || "user");
}

/** The permission keys this session holds. */
export function getPermissions() {
  try {
    const raw = JSON.parse(localStorage.getItem("permissions"));
    return Array.isArray(raw) ? raw : [];
  } catch (error) {
    console.error("Error parsing permissions from localStorage:", error);
    return [];
  }
}

/**
 * Does this session hold any of the given permissions?
 *
 * Any, not all, matching requirePermission() on the server — a screen that
 * needs either of two capabilities asks for both and is satisfied by one.
 * Called with nothing, it is true: an ungated screen is open to anyone signed
 * in, which is how the personal screens stay reachable.
 */
export function can(...keys) {
  if (!keys.length) return true;
  const held = getPermissions();
  return keys.flat().some((key) => held.includes(key));
}

/**
 * Does this session hold *every* one of the given permissions?
 *
 * For a screen that genuinely needs two things at once — the Invoice Summary
 * reads both the invoice list and the project list, and is useless with only
 * one of them. Offering it with a single permission would put a link in the
 * sidebar that leads to a half-broken page.
 */
export function canAll(...keys) {
  const flat = keys.flat();
  if (!flat.length) return true;
  const held = getPermissions();
  return flat.every((key) => held.includes(key));
}

/**
 * Which shell this session belongs in: "admin" or "user".
 *
 * Falls back to the stored role's own portal, and then to "user" — the
 * narrower of the two, so a session saved before this field existed lands
 * somewhere safe rather than in the admin area.
 */
export function getPortal(user = getStoredUser()) {
  const stored = localStorage.getItem("portal");
  if (stored === "admin" || stored === "user") return stored;
  const rolePortal = user?.role_id?.portal;
  return rolePortal === "admin" ? "admin" : "user";
}

export function isAdminPortal(user = getStoredUser()) {
  return getPortal(user) === "admin";
}

/** Where to send someone after signing in. */
export function getHomePath(user = getStoredUser()) {
  return isAdminPortal(user) ? "/admin" : "/user";
}

export function getDisplayName(user = getStoredUser()) {
  return user?.name || user?.username || user?.email || "Account";
}

export function getInitials(user = getStoredUser()) {
  const name = getDisplayName(user);
  if (name === "Account") return "?";
  return name
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");
}

export function logout(navigate) {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  localStorage.removeItem("permissions");
  localStorage.removeItem("portal");
  if (navigate) navigate("/");
  window.location.reload();
}
